// Pricing — three tiers + billing FAQ on its own route (FIX-13 real page).
import { useNavigate } from "react-router-dom";
import { Check } from "lucide-react";
import { C, SectionLabel, PublicPage } from "@/components/PublicShell";

export default function PricingPage() {
  const navigate = useNavigate();
  const cta = () => navigate("/sign-up");

  return (
    <PublicPage>
      <section className="py-16 px-5" data-testid="pricing-page">
        <div className="max-w-6xl mx-auto">
          <SectionLabel text="Pricing" color={C.muted} />
          <h1 className="text-xl sm:text-2xl font-extrabold tracking-tight mb-3" style={{ color: C.text }}>
            Three tiers. Pick what fits how you trade today.
          </h1>
          <p className="text-xs mb-10 max-w-xl" style={{ color: C.muted }}>
            Every new account starts with a 15-day Demo of the full MarkovLab tier.
            No credit card. Broker connections are included. Upgrade to keep going,
            or join the Pro waitlist for labs still in the pipeline.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

            {/* Demo */}
            <div className="rounded-lg p-6 flex flex-col"
              style={{ background: C.card, border: `1px solid ${C.border}` }}
              data-testid="pricing-demo">
              <div className="flex items-baseline justify-between mb-4">
                <span className="text-sm font-bold" style={{ color: C.text, fontFamily: "'JetBrains Mono', monospace" }}>Demo</span>
                <span className="text-2xl font-extrabold" style={{ color: C.text }}>
                  $0<span className="text-xs font-normal" style={{ color: C.muted }}> / 15 days</span>
                </span>
              </div>
              <p className="text-xs mb-4" style={{ color: C.muted }}>
                Full-access trial to MarkovLab. Everything unlocks for 15 days.
              </p>
              <ul className="space-y-2 text-xs flex-1" style={{ color: C.muted }}>
                {[
                  "Scanner + Wheel Income Engine",
                  "Portfolio, Position Management, Trade Log, Backtest Lab",
                  "Broker connections included",
                  "Auto-pauses on day 16 — your data stays",
                ].map(l => (
                  <li key={l} className="flex items-start gap-2">
                    <Check className="w-3 h-3 mt-0.5 shrink-0" style={{ color: C.accent }} /><span>{l}</span>
                  </li>
                ))}
              </ul>
              <button onClick={cta} data-testid="pricing-demo-cta"
                className="mt-6 w-full h-9 text-xs font-semibold rounded-md transition-opacity hover:opacity-85"
                style={{ background: "transparent", color: C.text, border: `1px solid ${C.border}` }}>
                Start free trial
              </button>
            </div>

            {/* MarkovLab — highlighted */}
            <div className="rounded-lg p-6 relative flex flex-col"
              style={{ background: C.card, border: `2px solid ${C.accent}` }}
              data-testid="pricing-markovlab">
              <span className="absolute -top-2.5 left-6 text-[9px] font-bold uppercase tracking-[0.2em] px-2 py-0.5 rounded"
                style={{ background: C.accent, color: "#0a0a0a", fontFamily: "'JetBrains Mono', monospace" }}>
                Most popular
              </span>
              <div className="flex items-baseline justify-between mb-4">
                <span className="text-sm font-bold" style={{ color: C.text, fontFamily: "'JetBrains Mono', monospace" }}>MarkovLab</span>
                <span className="text-2xl font-extrabold" style={{ color: C.text }}>
                  Priced at checkout<span className="text-xs font-normal" style={{ color: C.muted }}> / month</span>
                </span>
              </div>
              <p className="text-xs mb-4" style={{ color: C.muted }}>
                Options-first income platform — plus portfolio analytics most dashboards skip.
              </p>
              <ul className="space-y-2 text-xs flex-1" style={{ color: C.muted }}>
                {[
                  "Wheel, Scanner, Strike selection",
                  "Portfolio, Position Management, Trade Log",
                  "Backtest Lab and hedge-system-style tools",
                  "Broker connections included",
                  "Cancel anytime via Stripe",
                ].map(l => (
                  <li key={l} className="flex items-start gap-2">
                    <Check className="w-3 h-3 mt-0.5 shrink-0" style={{ color: C.accent }} /><span>{l}</span>
                  </li>
                ))}
              </ul>
              <button onClick={cta} data-testid="pricing-markovlab-cta"
                className="mt-6 w-full h-9 text-xs font-semibold rounded-md transition-opacity hover:opacity-85"
                style={{ background: C.accent, color: "#0a0a0a" }}>
                Get MarkovLab
              </button>
              <p className="mt-3 text-[10px]" style={{ color: "#555" }}>Billed monthly in USD via Stripe. Your card statement will show MARKOVLAB.</p>
            </div>

            {/* Pro — coming soon / waitlist */}
            <div className="rounded-lg p-6 relative flex flex-col"
              style={{ background: C.card, border: `1px solid ${C.cyan}`, opacity: 0.95 }}
              data-testid="pricing-pro">
              <span className="absolute -top-2.5 left-6 text-[9px] font-bold uppercase tracking-[0.2em] px-2 py-0.5 rounded"
                style={{ background: "rgba(79,195,247,0.15)", color: C.cyan,
                         border: `1px solid ${C.cyan}`, fontFamily: "'JetBrains Mono', monospace" }}>
                In development
              </span>
              <div className="flex items-baseline justify-between mb-4">
                <span className="text-sm font-bold" style={{ color: C.text, fontFamily: "'JetBrains Mono', monospace" }}>Pro</span>
              </div>
              <p className="text-xs mb-4" style={{ color: C.muted }}>
                Advanced features and labs in development — not yet available for purchase.
              </p>
              <ul className="space-y-2 text-xs flex-1" style={{ color: C.muted }}>
                {[
                  "Everything in MarkovLab",
                  "Advanced features and labs in development",
                  "Reliability Lab, Hedging Lab, Model Screener (as they ship)",
                ].map(l => (
                  <li key={l} className="flex items-start gap-2">
                    <Check className="w-3 h-3 mt-0.5 shrink-0" style={{ color: C.cyan }} /><span>{l}</span>
                  </li>
                ))}
              </ul>
              <button onClick={cta} data-testid="pricing-pro-cta"
                className="mt-6 w-full h-9 text-xs font-semibold rounded-md transition-colors hover:bg-cyan-500/10"
                style={{ background: "transparent", color: C.cyan, border: `1px solid ${C.cyan}` }}>
                Join Pro waitlist
              </button>
              <p className="mt-3 text-[10px]" style={{ color: "#555" }}>Pro is still in development. Nothing will be charged — join the waitlist to be notified when it&apos;s ready.</p>
            </div>
          </div>

          {/* FIX-12 — How billing works FAQ */}
          <div className="mt-12 max-w-3xl" data-testid="billing-faq">
            <h3 className="text-sm font-bold mb-4" style={{ color: C.text, fontFamily: "'JetBrains Mono', monospace" }}>How billing works</h3>
            <div className="space-y-4 text-xs" style={{ color: C.muted }}>
              {[
                ["What happens after the 15-day demo?", "Your account auto-pauses on day 16. Your data is kept, and nothing is ever charged unless you actively subscribe. The price shown at checkout reflects current promotions."],
                ["Do you store my card?", "No card is collected for the demo. If you subscribe, payment details are handled entirely by Stripe — we never see or store your card number."],
                ["How do I cancel?", "Self-service, anytime: open your Plans page and click Manage subscription to reach the Stripe customer portal. You keep access until the end of the period you've paid for."],
                ["Do you offer refunds?", "Fees already charged are non-refundable except for billing errors and where required by law (EEA/UK consumers have a 14-day withdrawal right). Full details in our Refund & Cancellation Policy."],
                ["What will appear on my card statement?", "MARKOVLAB. Subscriptions are billed in USD; the billing entity is identified on the checkout page and in our legal pages."],
              ].map(([q, a]) => (
                <div key={q}>
                  <div className="font-semibold mb-0.5" style={{ color: C.text }}>{q}</div>
                  <div className="leading-relaxed">{a}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </PublicPage>
  );
}
